import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { StickyCard, StickyCardSkeleton } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { PlatformRole } from 'src/models/platform/_types';
import { updatePlatformSettings } from 'src/models/platform/platform';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import { useTrainerSettings } from '../NewStaff/useTrainerSettings';

import { RoleEditor } from './RoleEditor';

const RolesManagementPage = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'staff.rolesManagement',
  });
  const { t: tGlobal } = useTranslation('translation');

  const { session, platformSettings, refreshSessionAndUser } = useSession();
  const { allReports } = useTrainerSettings();
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [originalRoles, setOriginalRoles] = useState<string | null>(null);

  const updatePlatformSettingsMutation = useMutation({
    mutationFn: (data: Partial<NonNullable<typeof platformSettings>>) =>
      updatePlatformSettings(platformSettings!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });

  if (!session || !platformSettings) {
    return <StickyCardSkeleton />;
  }

  // Store original roles for reset functionality (same as v2)
  if (!originalRoles) {
    setOriginalRoles(
      JSON.stringify(platformSettings.defaultChildPlatformRoles || [])
    );
  }

  const initialValues = {
    defaultChildPlatformRoles: (
      platformSettings.defaultChildPlatformRoles || []
    ).map((role, index) => ({
      ...role,
      id: role.id || `existing-role-${index}`,
      permissions: {
        assessmentsRole: role.permissions?.assessmentsRole || 0,
        schedulesRole: role.permissions?.schedulesRole || 0,
        messagesRole: role.permissions?.messagesRole || 1,
        resourcesRole: role.permissions?.resourcesRole || 0,
        videosRole: role.permissions?.videosRole || 0,
        routinesRole: role.permissions?.routinesRole || 0,
        subtrainerRole: role.permissions?.subtrainerRole || 0,
        clockRole: role.permissions?.clockRole || 0,
        connectedAppsRole: role.permissions?.connectedAppsRole || 0,
        workoutPlansRole: role.permissions?.workoutPlansRole || 0,
        exercisesRole: role.permissions?.exercisesRole || 0,
        clientsRole: role.permissions?.clientsRole || 0,
        groupsRole: role.permissions?.groupsRole || 0,
        schedulingRole: role.permissions?.schedulingRole || 0,
        cancelVisitsRole: role.permissions?.cancelVisitsRole || 0,
        clientPackagesRole: role.permissions?.clientPackagesRole || 0,
        inventoryRole: role.permissions?.inventoryRole || 0,
        disabledTabs: role.permissions?.disabledTabs || [],
        disabledDashboardBoxes: role.permissions?.disabledDashboardBoxes || [],
        disabledEmails: role.permissions?.disabledEmails || [],
        disabledDashboardNotifications:
          role.permissions?.disabledDashboardNotifications || [],
        headTrainerEmails: role.permissions?.headTrainerEmails || [],
        reportsRole:
          role.permissions?.reportsRole ||
          allReports.map((report) => report.id.toString()),
      },
    })),
    platformSettings: {
      blockSitesToAddRoles: platformSettings.blockSitesToAddRoles || false,
      blockSitesToEditRoles: platformSettings.blockSitesToEditRoles || false,
    },
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setLoading(true);
    try {
      // Update platform settings with block settings and default child platform roles
      await updatePlatformSettingsMutation.mutateAsync({
        defaultChildPlatformRoles: values.defaultChildPlatformRoles.map(
          (role) => ({
            id: role.id,
            name: role.name,
            permissions: role.permissions,
          })
        ),
        blockSitesToAddRoles: values.platformSettings.blockSitesToAddRoles,
        blockSitesToEditRoles: values.platformSettings.blockSitesToEditRoles,
      });

      await refreshSessionAndUser();

      // Clear original roles after successful save (same as v2)
      setOriginalRoles(null);

      toast.success(tGlobal('settings.platform.customizationSettingsSaved'));
    } catch (error) {
      toast.error(api.formatError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (originalRoles) {
      // Reset to original roles
      setOriginalRoles(null);
      toast.success(t('changesDiscarded'));
    }
  };

  const formikProps = {
    initialValues,
    enableReinitialize: true,
    validationSchema: null,
    onSubmit: handleSubmit,
  };

  return (
    <Formik {...formikProps}>
      {({ values, setFieldValue, submitForm }) => {
        return (
          <Stack spacing={3}>
            <StickyCard
              title={
                <Stack spacing={1}>
                  <Typography variant="h4">{t('title')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('description')}
                  </Typography>
                </Stack>
              }
              actions={
                <Stack direction="row" spacing={2}>
                  <Button variant="outlined" onClick={handleReset}>
                    {t('reset')}
                  </Button>
                  <Button
                    variant="contained"
                    loading={loading}
                    onClick={() => submitForm()}
                  >
                    {t('save')}
                  </Button>
                </Stack>
              }
            >
              <Stack spacing={3}>
                {/* HQ Settings Section - only show for HQ platforms */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('hqSettings')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('hqDescription')}
                  </Typography>
                  <Stack spacing={2}>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={!values.platformSettings.blockSitesToAddRoles}
                        onChange={(e) =>
                          setFieldValue(
                            'platformSettings.blockSitesToAddRoles',
                            !e.target.checked
                          )
                        }
                      />
                      <Typography variant="body2" ml={1}>
                        {t('allowSitesAddRoles')}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={!values.platformSettings.blockSitesToEditRoles}
                        onChange={(e) =>
                          setFieldValue(
                            'platformSettings.blockSitesToEditRoles',
                            !e.target.checked
                          )
                        }
                      />
                      <Typography variant="body2" ml={1}>
                        {t('allowSitesEditRoles')}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* No Roles Message */}
                {(!values.defaultChildPlatformRoles ||
                  values.defaultChildPlatformRoles.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {t('noRolesMessage')}
                  </Typography>
                )}

                {/* Role Editors */}
                {values.defaultChildPlatformRoles?.map((role, index) => (
                  <RoleEditor
                    key={role.id}
                    index={index}
                    onDelete={() => {
                      const newRoles = values.defaultChildPlatformRoles?.filter(
                        (_, i) => i !== index
                      );
                      setFieldValue('defaultChildPlatformRoles', newRoles);
                    }}
                    canEdit={!values.platformSettings.blockSitesToEditRoles}
                  />
                ))}

                {/* Create New Role Button */}
                <Button
                  variant="outlined"
                  startIcon={<Icon icon={icon({ name: 'plus' })} />}
                  onClick={() => {
                    const newRole: PlatformRole = {
                      id: `new-role-${Date.now()}-${Math.random()
                        .toString(36)
                        .substr(2, 9)}`,
                      name: 'New Role',
                      permissions: {
                        assessmentsRole: 0,
                        schedulesRole: 0,
                        messagesRole: 1,
                        resourcesRole: 0,
                        videosRole: 0,
                        routinesRole: 0,
                        subtrainerRole: 0,
                        clockRole: 0,
                        connectedAppsRole: 0,
                        workoutPlansRole: 0,
                        exercisesRole: 0,
                        clientsRole: 0,
                        groupsRole: 0,
                        schedulingRole: 0,
                        cancelVisitsRole: 0,
                        clientPackagesRole: 0,
                        inventoryRole: 0,
                        disabledTabs: [],
                        disabledDashboardBoxes: [],
                        disabledEmails: [],
                        disabledDashboardNotifications: [],
                        headTrainerEmails: [],
                        reportsRole: allReports.map((report) =>
                          report.id.toString()
                        ),
                      },
                    };
                    setFieldValue('defaultChildPlatformRoles', [
                      ...(values.defaultChildPlatformRoles || []),
                      newRole,
                    ]);
                  }}
                >
                  {t('createNewRole')}
                </Button>
              </Stack>
            </StickyCard>
          </Stack>
        );
      }}
    </Formik>
  );
};

export default RolesManagementPage;
